import { useState } from 'react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import {
  useTimeSlots,
  useCreateTimeSlot,
  useUpdateTimeSlot,
  useDeleteTimeSlot,
} from '@/hooks/useAgenda'
import { useAuth } from '@/contexts/AuthContext'
import type { AgendaTimeSlot } from '@/services/agenda'
import { weekdayLabels } from '@/types/agenda'
import {
  Plus,
  Trash2,
  Clock,
  ToggleLeft,
  ToggleRight,
  Pencil,
} from 'lucide-react'
import toast from 'react-hot-toast'

// Days to display (Lundi=1 to Samedi=6, skip Dimanche=0)
const WORKING_DAYS = [1, 2, 3, 4, 5, 6]

interface SlotFormData {
  day_of_week: number
  start_time: string
  end_time: string
}

export function TimeSlotManagement() {
  const { user } = useAuth()
  const adminId = user?.id

  const { data: timeSlots = [], isLoading } = useTimeSlots(adminId)
  const createSlot = useCreateTimeSlot()
  const updateSlot = useUpdateTimeSlot()
  const deleteSlot = useDeleteTimeSlot()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingSlot, setEditingSlot] = useState<AgendaTimeSlot | null>(null)
  const [formData, setFormData] = useState<SlotFormData>({
    day_of_week: 1,
    start_time: '09:00',
    end_time: '12:00',
  })

  // Group slots by day
  const slotsByDay: Record<number, AgendaTimeSlot[]> = {}
  for (const day of WORKING_DAYS) {
    slotsByDay[day] = timeSlots
      .filter((s) => s.day_of_week === day)
      .sort((a, b) => a.start_time.localeCompare(b.start_time))
  }

  const openCreateModal = (day?: number) => {
    setEditingSlot(null)
    setFormData({
      day_of_week: day ?? 1,
      start_time: '09:00',
      end_time: '12:00',
    })
    setIsModalOpen(true)
  }

  const openEditModal = (slot: AgendaTimeSlot) => {
    setEditingSlot(slot)
    setFormData({
      day_of_week: slot.day_of_week,
      start_time: slot.start_time.slice(0, 5), // HH:MM
      end_time: slot.end_time.slice(0, 5),
    })
    setIsModalOpen(true)
  }

  const handleSubmit = async () => {
    if (!adminId) return

    if (formData.start_time >= formData.end_time) {
      toast.error("L'heure de fin doit être après l'heure de début")
      return
    }

    try {
      if (editingSlot) {
        await updateSlot.mutateAsync({
          id: editingSlot.id,
          updates: {
            day_of_week: formData.day_of_week,
            start_time: formData.start_time + ':00',
            end_time: formData.end_time + ':00',
          },
        })
        toast.success('Créneau mis à jour')
      } else {
        await createSlot.mutateAsync({
          admin_id: adminId,
          day_of_week: formData.day_of_week,
          start_time: formData.start_time + ':00',
          end_time: formData.end_time + ':00',
          is_active: true,
        })
        toast.success('Créneau ajouté')
      }
      setIsModalOpen(false)
    } catch {
      toast.error('Erreur lors de la sauvegarde')
    }
  }

  const handleToggleActive = async (slot: AgendaTimeSlot) => {
    try {
      await updateSlot.mutateAsync({
        id: slot.id,
        updates: { is_active: !slot.is_active },
      })
      toast.success(slot.is_active ? 'Créneau désactivé' : 'Créneau activé')
    } catch {
      toast.error('Erreur lors de la mise à jour')
    }
  }

  const handleDelete = async (slot: AgendaTimeSlot) => {
    try {
      await deleteSlot.mutateAsync(slot.id)
      toast.success('Créneau supprimé')
    } catch {
      toast.error('Erreur lors de la suppression')
    }
  }

  const formatTime = (time: string) => time.slice(0, 5)

  if (isLoading) {
    return (
      <Card>
        <div className="p-8 text-center text-brown-500">
          Chargement des créneaux...
        </div>
      </Card>
    )
  }

  const totalSlots = timeSlots.length
  const activeSlots = timeSlots.filter((s) => s.is_active).length

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-brown-900">
            Créneaux de disponibilité
          </h2>
          <p className="text-sm text-brown-500">
            {totalSlots === 0
              ? 'Aucun créneau configuré'
              : `${activeSlots} créneau${activeSlots > 1 ? 'x' : ''} actif${activeSlots > 1 ? 's' : ''} sur ${totalSlots}`}
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => openCreateModal()}
          leftIcon={<Plus className="w-4 h-4" />}
        >
          Ajouter un créneau
        </Button>
      </div>

      {/* Weekly grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {WORKING_DAYS.map((day) => (
          <Card key={day} padding="sm">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-brown-900">
                {weekdayLabels[day]}
              </h3>
              <button
                onClick={() => openCreateModal(day)}
                className="text-mint-600 hover:text-mint-700 transition-colors"
                title={`Ajouter un créneau le ${weekdayLabels[day]}`}
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>

            {slotsByDay[day].length === 0 ? (
              <p className="text-sm text-brown-400 italic py-2">
                Aucun créneau
              </p>
            ) : (
              <div className="space-y-2">
                {slotsByDay[day].map((slot) => (
                  <div
                    key={slot.id}
                    className={`flex items-center justify-between p-2 rounded-lg border transition-colors ${
                      slot.is_active
                        ? 'bg-mint-50 border-mint-200'
                        : 'bg-beige-50 border-beige-200 opacity-60'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5 text-brown-400" />
                      <span
                        className={`text-sm font-medium ${
                          slot.is_active ? 'text-brown-900' : 'text-brown-500 line-through'
                        }`}
                      >
                        {formatTime(slot.start_time)} - {formatTime(slot.end_time)}
                      </span>
                    </div>

                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleToggleActive(slot)}
                        className={`p-1 rounded transition-colors ${
                          slot.is_active
                            ? 'text-mint-600 hover:text-mint-700'
                            : 'text-brown-400 hover:text-brown-600'
                        }`}
                        title={slot.is_active ? 'Désactiver' : 'Activer'}
                      >
                        {slot.is_active ? (
                          <ToggleRight className="w-4 h-4" />
                        ) : (
                          <ToggleLeft className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => openEditModal(slot)}
                        className="p-1 rounded text-brown-400 hover:text-brown-600 transition-colors"
                        title="Modifier"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(slot)}
                        className="p-1 rounded text-brown-400 hover:text-red-600 transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>

      {/* Dimanche (optional, collapsed) */}
      {slotsByDay[0] !== undefined && (
        <Card padding="sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-medium text-brown-900">Dimanche</h3>
            <button
              onClick={() => openCreateModal(0)}
              className="text-mint-600 hover:text-mint-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          {(timeSlots.filter((s) => s.day_of_week === 0).length === 0) ? (
            <p className="text-sm text-brown-400 italic">Fermé</p>
          ) : (
            <div className="space-y-2">
              {timeSlots
                .filter((s) => s.day_of_week === 0)
                .map((slot) => (
                  <div
                    key={slot.id}
                    className={`flex items-center justify-between p-2 rounded-lg border ${
                      slot.is_active
                        ? 'bg-mint-50 border-mint-200'
                        : 'bg-beige-50 border-beige-200 opacity-60'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5 text-brown-400" />
                      <span className="text-sm font-medium">
                        {formatTime(slot.start_time)} - {formatTime(slot.end_time)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleToggleActive(slot)}
                        className={`p-1 rounded ${
                          slot.is_active ? 'text-mint-600' : 'text-brown-400'
                        }`}
                      >
                        {slot.is_active ? (
                          <ToggleRight className="w-4 h-4" />
                        ) : (
                          <ToggleLeft className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => openEditModal(slot)}
                        className="p-1 rounded text-brown-400 hover:text-brown-600"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(slot)}
                        className="p-1 rounded text-brown-400 hover:text-red-600"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </Card>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingSlot ? 'Modifier le créneau' : 'Nouveau créneau'}
        size="sm"
      >
        <div className="space-y-4">
          {/* Day selector */}
          <div>
            <label className="block text-sm font-medium text-brown-700 mb-1">
              Jour
            </label>
            <select
              value={formData.day_of_week}
              onChange={(e) =>
                setFormData((f) => ({ ...f, day_of_week: Number(e.target.value) }))
              }
              className="w-full rounded-lg border border-beige-300 px-3 py-2 text-brown-900 focus:border-mint-500 focus:ring-1 focus:ring-mint-500"
            >
              {[1, 2, 3, 4, 5, 6, 0].map((d) => (
                <option key={d} value={d}>
                  {weekdayLabels[d]}
                </option>
              ))}
            </select>
          </div>

          {/* Time range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-brown-700 mb-1">
                Début
              </label>
              <input
                type="time"
                value={formData.start_time}
                onChange={(e) =>
                  setFormData((f) => ({ ...f, start_time: e.target.value }))
                }
                className="w-full rounded-lg border border-beige-300 px-3 py-2 text-brown-900 focus:border-mint-500 focus:ring-1 focus:ring-mint-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-brown-700 mb-1">
                Fin
              </label>
              <input
                type="time"
                value={formData.end_time}
                onChange={(e) =>
                  setFormData((f) => ({ ...f, end_time: e.target.value }))
                }
                className="w-full rounded-lg border border-beige-300 px-3 py-2 text-brown-900 focus:border-mint-500 focus:ring-1 focus:ring-mint-500"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsModalOpen(false)}
            >
              Annuler
            </Button>
            <Button
              size="sm"
              onClick={handleSubmit}
              isLoading={createSlot.isPending || updateSlot.isPending}
            >
              {editingSlot ? 'Enregistrer' : 'Ajouter'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
